import { useMemo } from 'react'
import { useSlsStateContext, getNestedSubelementsByType, getImage, getNestedOrFloatingElementById } from '@fs/zion-sls'
import { getPersonsFromElement, getSelectedAndDisplayPersonsByArk } from '../utils/linkablePersonsUtils'

/**
 * Custom hook to organize person data for the link names panel
 *
 * Supports two modes:
 * 1. Form data mode: Pass linkedPersonIds array with linked person objects from form state (reflects unsaved changes)
 *    - The calling card extracts linked persons from its form structure and passes them here
 *    - Ensures the panel reflects current form state, not saved SLS state
 * 2. SLS mode: Pass linkedPersonIds=null to query SLS directly (reflects saved state)
 *    - Default behavior for backward compatibility
 *
 * @param {Object} person - The selected person object
 * @param {Object} field - The field element to link names to (can be from form state or SLS)
 * @param {Array<{id: string, partitionKey: string}>|Array<String>} linkedPersonIds - Optional. Array of linked person objects [{id, partitionKey}] or person ID strings (for form mode). Pass null for SLS mode.
 * @returns {Object} Organized person data by image, record, and ark
 */
export const useLinkablePersons = ({ person, field, linkedPersonIds = null }) => {
  const { images, currentImageArk } = useSlsStateContext()

  const currentPersonRecord = useMemo(
    () => getNestedOrFloatingElementById(images, person?.recordId),
    [images, person?.recordId]
  )

  const currentImage = useMemo(() => getImage(images, currentImageArk), [images, currentImageArk])

  const personsInThisImage = useMemo(() => {
    if (!currentImage) return []
    // Filter is necessary to exclude floating persons that may be attached to other arks
    return getNestedSubelementsByType(currentImage, 'PERSON').filter((p) => p.partitionKey === currentImageArk)
  }, [currentImageArk, currentImage])

  const personsInThisRecord = useMemo(
    () => currentPersonRecord?.subElements?.filter((se) => se.elementType === 'PERSON') || [],
    [currentPersonRecord?.subElements]
  )

  const personsLinkedToElement = useMemo(
    () => getPersonsFromElement({ element: field, images, selectedPersonId: person.id, linkedPersonIds }),
    [field, person.id, images, linkedPersonIds]
  )

  const personsByImage = useMemo(
    () => getSelectedAndDisplayPersonsByArk({ allPersonsSharingElement: personsLinkedToElement, personsInThisRecord }),
    [personsLinkedToElement, personsInThisRecord]
  )

  const personsInOtherImages = useMemo(() => {
    if (!personsByImage) return {}
    const { [currentImageArk]: _, ...rest } = personsByImage
    return rest
  }, [personsByImage, currentImageArk])

  const personsLinkedToElementOnCurrentImage = useMemo(
    () => personsByImage?.[currentImageArk]?.selected || [],
    [personsByImage, currentImageArk]
  )

  return {
    personsInThisImage,
    personsInThisRecord,
    personsInOtherImages,
    personsLinkedToElementOnCurrentImage,
  }
}
